import React, { useMemo } from 'react';
import { useSelector, shallowEqual, useIntl } from 'umi';
import { HIDDEN_ORGANIZATIONS } from '.';
import { TOrganizationTextKey } from './types';
import { LANGUAGE_EN, LANGUAGE_CN } from '@/pages/dashboard/_config/constant';

/**
 * 文本映射配置
 * 根据 textKey、语言环境和组织类型返回对应的文本
 * 结构: { textKey: { locale: { default: string, techland: string } } }
 */
const TEXT_MAP: Record<TOrganizationTextKey, { 
  [LANGUAGE_CN]: { default: string; techland: string; };
  [LANGUAGE_EN]: { default: string; techland: string; };
}> = {
  agentTitle: {
    [LANGUAGE_CN]: {
      default: 'DataBrain Agent',
      techland: 'Techland Agent',
    },
    [LANGUAGE_EN]: {
      default: 'DataBrain Agent',
      techland: 'Techland Agent',
    },
  },
  agentHomeSybTitle: {
    [LANGUAGE_CN]: {
      default: '',
      techland: 'Techland Agent目前仍在开发中，功能正在持续优化',
    },
    [LANGUAGE_EN]: {
      default: '',
      techland: 'This agent is a work-in-progress project, with features still being refined.',
    },
  },
};

interface IOrganizationTextProps {
  /** 文本映射键，用于指定使用哪个文本配置 */
  textKey: TOrganizationTextKey;
  /** 自定义渲染函数，接收处理后的文本作为参数 */
  children?: (text: string) => React.ReactNode;
  /** 如果不提供 children，则直接渲染文本 */
  className?: string;
}

/**
 * 组织文本转换组件
 * 根据用户所属组织动态替换文本内容
 * 
 * @example
 * // 基础用法 - 直接渲染文本
 * <OrganizationText textKey="agentTitle" />
 * 
 * @example
 * // 使用 children 函数自定义渲染
 * <OrganizationText textKey="agentTitle">
 *   {(text) => <span className="logo-text">{text}</span>}
 * </OrganizationText>
 * 
 * @example
 * // 在复杂组件中使用
 * <OrganizationText textKey="agentTitle">
 *   {(text) => (
 *     <div className="header">
 *       <img src={logo} alt={text} />
 *       <span>{text}</span>
 *     </div>
 *   )}
 * </OrganizationText>
 */
export const OrganizationText: React.FC<IOrganizationTextProps> = ({
  textKey,
  children,
  className,
}) => {
  const { locale } = useIntl();
  const userInfo = useSelector(
    (state: any) => state.home.userInfo,
    shallowEqual,
  );

  // 获取用户的组织列表
  const userOrganizations: string[] = userInfo?.organization || [];

  // 判断用户是否属于需要替换文本的组织
  const shouldReplaceText = useMemo(() => userOrganizations.some((org: string) =>
    HIDDEN_ORGANIZATIONS.includes(org),
  ), [userOrganizations]);

  // 获取最终要显示的文本
  const displayText = useMemo(() => {
    const textConfig = TEXT_MAP[textKey];
    if (!textConfig) {
      console.warn(`Invalid textKey: ${textKey}, using default text`);
      return '';
    }

    // 获取当前语言环境的配置，如果不存在则使用中文配置
    const localeConfig = textConfig[locale as typeof LANGUAGE_CN | typeof LANGUAGE_EN] || textConfig[LANGUAGE_CN];
    
    // 根据组织类型返回对应的文本
    return shouldReplaceText ? localeConfig.techland : localeConfig.default;
  }, [textKey, shouldReplaceText, locale]);

  // 如果提供了 children 函数，使用它来渲染
  if (children && typeof children === 'function') {
    return <>{children(displayText)}</>;
  }

  // 否则直接渲染文本
  return <span className={className}>{displayText}</span>;
};